import { FormEvent, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { getRuntimeConfig } from '../app/config/runtime-config';
import { Route42Logo } from '../components/brand/Route42Logo';
import { confirmCognitoPasswordReset, requestCognitoPasswordReset } from './auth-client';

type RecoveryStep = 'request' | 'confirm' | 'complete';

function routeRecoveryStep(value: string | null): Exclude<RecoveryStep, 'complete'> {
  return value === 'confirm' ? 'confirm' : 'request';
}

export function ForgotPasswordPage() {
  const config = getRuntimeConfig();
  const [searchParams, setSearchParams] = useSearchParams();
  const [username, setUsername] = useState(searchParams.get('username')?.trim() ?? '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [complete, setComplete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(
    routeRecoveryStep(searchParams.get('step')) === 'confirm'
      ? 'Enter the verification code from your Route42 password-reset email.'
      : null,
  );

  const step: RecoveryStep = complete ? 'complete' : routeRecoveryStep(searchParams.get('step'));

  const title = useMemo(() => {
    if (step === 'complete') return 'Password updated';
    if (step === 'confirm') return 'Reset your password';
    return 'Forgot your password?';
  }, [step]);

  const recoveryNotice = notice ?? (
    step === 'confirm'
      ? 'If an account exists for that address, we sent password-reset instructions.'
      : null
  );

  function showConfirmationStep(normalizedUsername: string) {
    setNotice('If an account exists for that address, we sent password-reset instructions.');
    setSearchParams(
      { step: 'confirm', username: normalizedUsername },
      { replace: true },
    );
  }

  async function requestReset() {
    const normalizedUsername = username.trim();
    if (!normalizedUsername) {
      setError('Enter your Route42 email or username.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      if (config.authMode !== 'mock') {
        try {
          await requestCognitoPasswordReset(normalizedUsername);
        } catch (reason) {
          // Do not disclose whether a Route42 identity exists. Cognito can still
          // return UserNotFoundException for ForgotPassword in some pool modes.
          if (!(reason instanceof Error) || reason.name !== 'UserNotFoundException') throw reason;
        }
      }
      showConfirmationStep(normalizedUsername);
    } catch {
      setError('Route42 could not start password recovery right now. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmReset() {
    const normalizedUsername = username.trim();
    const normalizedCode = code.trim();
    if (!normalizedUsername || !normalizedCode || !newPassword) {
      setError('Enter your email or username, verification code, and new password.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      if (config.authMode !== 'mock') {
        await confirmCognitoPasswordReset(normalizedUsername, normalizedCode, newPassword);
      }
      setNotice(null);
      setComplete(true);
    } catch (reason) {
      const name = reason instanceof Error ? reason.name : '';
      if (name === 'CodeMismatchException' || name === 'ExpiredCodeException') {
        setError('That verification code is incorrect or has expired. Request a new code and try again.');
      } else if (name === 'InvalidPasswordException') {
        setError(reason instanceof Error ? reason.message : 'The new password does not meet Route42 password requirements.');
      } else {
        setError('Route42 could not reset the password. Check the code and password, then try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  function submitRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!busy) void requestReset();
  }

  function submitConfirmation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!busy) void confirmReset();
  }

  return (
    <main
      className="r42-auth-page"
      data-testid="forgot-password-page"
      data-auth-mode={config.authMode}
    >
      <section className="r42-auth-card">
        <div className="r42-auth-identity">
          <div className="r42-auth-brand"><Route42Logo variant="lockup" /></div>
          <span className="r42-eyebrow r42-auth-tagline">Machine Intelligence</span>
        </div>

        <div className="r42-auth-task">
          <h1>{title}</h1>
          {step === 'request' ? <p>Enter your Route42 email or username and we’ll send reset instructions.</p> : null}
          {step === 'confirm' ? <p>Use the verification code from your email and choose a new password.</p> : null}
          {step === 'complete' ? <p>Your Route42 password has been updated. You can sign in with the new password.</p> : null}
        </div>

        {recoveryNotice ? (
          <div
            className="r42-form-notice"
            role="status"
            aria-live="polite"
            data-testid="password-recovery-notice"
          >
            {recoveryNotice}
          </div>
        ) : null}

        {step === 'request' ? (
          <form onSubmit={submitRequest} noValidate>
            <label>
              Email or username
              <input autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} />
            </label>
            {error ? <div className="r42-form-error" role="alert">{error}</div> : null}
            <button className="btn r42-btn-primary w-100" disabled={busy} type="submit">
              {busy ? 'Sending…' : 'Send reset instructions'}
            </button>
          </form>
        ) : null}

        {step === 'confirm' ? (
          <form onSubmit={submitConfirmation} noValidate>
            <label>
              Email or username
              <input autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} />
            </label>
            <label>
              Verification code
              <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(event) => setCode(event.target.value)} />
            </label>
            <label>
              New password
              <input type="password" autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} />
            </label>
            {error ? <div className="r42-form-error" role="alert">{error}</div> : null}
            <button className="btn r42-btn-primary w-100" disabled={busy} type="submit">
              {busy ? 'Updating…' : 'Reset password'}
            </button>
            <button
              className="r42-auth-secondary-action"
              type="button"
              disabled={busy}
              onClick={() => {
                setError(null);
                setNotice(null);
                setComplete(false);
                setSearchParams({}, { replace: true });
              }}
            >
              Request a new code
            </button>
          </form>
        ) : null}

        <div className="r42-auth-return">
          <Link to="/sign-in">{step === 'complete' ? 'Return to sign in' : 'Back to sign in'}</Link>
        </div>
      </section>
    </main>
  );
}
